import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { sendOtp, verifyOtp } from "../Services/api";
import { toast } from "react-toastify";
import Loader from './Loader';

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "ml", label: "മല" },
  { code: "kn", label: "ಕನ" },
];

const OTP_LENGTH = 6;

export default function OTPVerification({ mode, onSuccess }) {
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { t, i18n } = useTranslation();
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [displayDigits, setDisplayDigits] = useState(Array(OTP_LENGTH).fill(""));
  const maskTimers = useRef(Array(OTP_LENGTH).fill(null));
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const logo = process.env.REACT_APP_HOSPITAL_LOGO;
  const hospitalName = process.env.REACT_APP_PAY_HOSPITAL_NAME;
  const inputsRef = useRef([]);
  const hasFocusedRef = useRef(false);

  // Theme toggle (syncs with App.js via localStorage + classList)
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const toggleTheme = () => {
    setIsDark(prev => {
      const next = !prev;
      document.documentElement.classList.toggle("dark", next);
      localStorage.setItem("theme", next ? "dark" : "light");
      // Notify App.js so its isDark state stays in sync
      window.dispatchEvent(new CustomEvent("themeChange", { detail: { isDark: next } }));
      return next;
    });
  };

  // Pipeline animation state
  const [wfStage, setWfStage] = useState(0);
  const wfStageRef = useRef(0);
  const STAGE_DURATIONS = [5600, 2800, 2800, 2800, 2800];

  useEffect(() => {
    let timeout;
    const advance = () => {
      const next = (wfStageRef.current + 1) % 5;
      wfStageRef.current = next;
      setWfStage(next);
      timeout = setTimeout(advance, STAGE_DURATIONS[next]);
    };
    timeout = setTimeout(advance, STAGE_DURATIONS[0]);
    return () => clearTimeout(timeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatCooldown = (s) => {
    const m = Math.floor(s / 60).toString().padStart(2, "0");
    return `${m}:${(s % 60).toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (sent && !otpVerified && !hasFocusedRef.current) {
      hasFocusedRef.current = true;
      setTimeout(() => inputsRef.current[0]?.focus(), 80);
    }
  }, [sent, otpVerified]);

  useEffect(() => {
    let timer;
    if (cooldown > 0) timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleLangChange = (lang) => {
    i18n.changeLanguage(lang);
    document.documentElement.setAttribute("lang", lang);
  };

  const sendOTP = async () => {
    if (!/^\d{10}$/.test(phone)) {
      toast.warn("Please enter a valid 10-digit phone number");
      return;
    }
    const normalizedPhone = `+91${phone}`;
    try {
      setSending(true);
      const data = await sendOtp(normalizedPhone);
      if (data?.message || data?.success) {
        setSent(true);
        setCooldown(90);
        hasFocusedRef.current = false;
        toast.success(data.message || "OTP sent successfully!");
      } else {
        toast.error(data?.error || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error sending OTP"
      );
    } finally {
      setSending(false);
    }
  };

  const verifyOTP = async (enteredOtp) => {
    if (!enteredOtp || enteredOtp.length !== OTP_LENGTH) {
      toast.warn("Please enter complete OTP");
      return;
    }
    const normalizedPhone = `+91${phone}`;
    try {
      setVerifying(true);
      setLoadingMessage("Verifying OTP...");
      setLoading(true);
      const data = await verifyOtp(normalizedPhone, enteredOtp);
      if (data?.status === "success" || data?.success) {
        localStorage.setItem("otpVerified", "true");
        localStorage.setItem("phoneNumber", phone);
        localStorage.setItem("otpVerifiedAt", new Date().toISOString());
        localStorage.setItem("lastActiveAt", Date.now().toString());
        setOtpVerified(true);
        toast.success(data?.message || "OTP verified!");
        onSuccess("", phone);
      } else {
        toast.error(data?.error || data?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error verifying OTP"
      );
    } finally {
      setVerifying(false);
      setLoading(false);
    }
  };

  const maskDigitAt = (index) => {
    if (maskTimers.current[index]) clearTimeout(maskTimers.current[index]);
    maskTimers.current[index] = setTimeout(() => {
      setDisplayDigits(prev => {
        const d = [...prev];
        if (d[index] && d[index] !== "") d[index] = "•";
        return d;
      });
    }, 600);
  };

  const handleDigitChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, "").replace(/•/g, "");
    if (!val) return;
    const digit = val[val.length - 1];
    const newOtp = [...otpDigits];
    newOtp[index] = digit;
    setOtpDigits(newOtp);
    setDisplayDigits(prev => { const d = [...prev]; d[index] = digit; return d; });
    maskDigitAt(index);
    if (index < OTP_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const handleDigitKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      const newOtp = [...otpDigits];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtpDigits(newOtp);
        if (maskTimers.current[index]) clearTimeout(maskTimers.current[index]);
        setDisplayDigits(prev => { const d = [...prev]; d[index] = ""; return d; });
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newOtp = Array(OTP_LENGTH).fill("");
    const newDisplay = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((char, i) => { newOtp[i] = char; newDisplay[i] = char; });
    setOtpDigits(newOtp);
    setDisplayDigits(newDisplay);
    pasted.split("").forEach((_, i) => maskDigitAt(i));
    const lastIndex = Math.min(pasted.length - 1, OTP_LENGTH - 1);
    setTimeout(() => inputsRef.current[lastIndex]?.focus(), 50);
  };

  const otpComplete = otpDigits.every((d) => d !== "");
  const canSend = !otpVerified && !sending && cooldown === 0;

  return (
    <div className="otp-fullscreen">
      {loading && <Loader message={loadingMessage} />}

      {/* Dot pattern texture overlay */}
      <div className="otp-dot-overlay" />

      {/* Animated background blobs */}
      <div className="otp-blob otp-blob-1" />
      <div className="otp-blob otp-blob-2" />
      <div className="otp-blob otp-blob-3" />

      {/* Floating medical cross icons */}
      <div className="otp-med-icon" style={{ top: "5%",  right: "8%",  fontSize: "80px",  animationDelay: "0s" }}>+</div>
      <div className="otp-med-icon" style={{ bottom: "14%", left: "4%",  fontSize: "110px", animationDelay: "-3.5s" }}>+</div>
      <div className="otp-med-icon" style={{ top: "48%", right: "11%", fontSize: "55px",  animationDelay: "-6.5s" }}>+</div>

      {/* Top-right controls: theme toggle + language switcher */}
      <div className="otp-top-controls">
        <button
          className="otp-theme-toggle"
          onClick={toggleTheme}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
          style={{ touchAction: "manipulation" }}
        >
          {isDark ? "☀️" : "🌙"}
        </button>

        <div className="otp-lang-switcher">
          {LANGUAGES.map(({ code, label }) => (
            <button
              key={code}
              onClick={() => handleLangChange(code)}
              className={`otp-lang-btn ${i18n.language === code ? "otp-lang-btn-active" : "otp-lang-btn-inactive"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Main Card ── */}
      <div className="otp-card">

        {/* Logo + Branding */}
        <div className="otp-logo-section">
          <div className="otp-logo-ring">
            <img
              src={logo}
              alt={hospitalName || "Hospital"}
              style={{ width: 54, height: 54, objectFit: "contain", borderRadius: "50%" }}
            />
          </div>

          <h1 className="otp-hospital-name">{hospitalName}</h1>
          <p className="otp-app-name">{t("login.appName")}</p>
          <p className="otp-subtitle-text">{t("login.subtitle")}</p>

          <div className="otp-secure-badge">
            <span className="otp-secure-dot" />
            Secure Patient Portal
          </div>
        </div>

        {/* Phone Number Field */}
        <div className="otp-field-wrap">
          <label className="otp-field-label">
            {t("otp.mobileNumber") || "Mobile Number"}
          </label>
          <div className="otp-phone-group">
            <span className="otp-phone-prefix">
              🇮🇳 +91
            </span>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              placeholder="Enter 10-digit number"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              className="otp-phone-input"
              disabled={otpVerified}
              autoComplete="tel-national"
            />
            {phone.length === 10 && (
              <span className="otp-phone-valid">✓</span>
            )}
          </div>
          <div className="otp-char-count">{phone.length} / 10</div>
        </div>

        {/* Send OTP Button */}
        <button
          className={`otp-send-btn ${canSend ? "otp-send-btn-active" : "otp-send-btn-disabled"}`}
          onClick={sendOTP}
          disabled={!canSend}
        >
          {sending ? (
            <>
              <span className="otp-spinner" />
              Sending OTP...
              <div className="btn-shimmer" />
            </>
          ) : cooldown > 0 ? (
            <>
              <span style={{ fontSize: "1.05em" }}>⏱</span>
              Resend in {formatCooldown(cooldown)}
            </>
          ) : (
            <>
              {t("otp.send") || "Send OTP"}
              <span className="btn-arrow">→</span>
            </>
          )}
        </button>

        {/* OTP Entry Section */}
        {sent && !otpVerified && (
          <div className="otp-verify-section">
            {/* Sent confirmation */}
            <div className="otp-sent-notice">
              <span style={{ fontSize: "1.2rem", flexShrink: 0 }}>📱</span>
              <p className="otp-sent-text">
                OTP sent to +91&nbsp;{phone}
              </p>
            </div>

            {/* OTP label */}
            <label
              className="otp-field-label"
              style={{ textAlign: "center", display: "block", marginBottom: 12 }}
            >
              {t("otp.enterOtpLabel") || "Enter OTP"}
            </label>

            {/* Digit Inputs */}
            <div className="otp-digits-container">
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={displayDigits[index]}
                  onChange={(e) => handleDigitChange(e, index)}
                  onKeyDown={(e) => handleDigitKeyDown(e, index)}
                  onPaste={handlePaste}
                  className={`otp-digit ${digit ? "otp-digit-filled" : ""} ${displayDigits[index] === "•" ? "otp-digit-masked" : ""}`}
                  autoComplete={index === 0 ? "one-time-code" : "off"}
                />
              ))}
            </div>

            {/* Verify Button */}
            <button
              onClick={() => verifyOTP(otpDigits.join(""))}
              disabled={verifying || !otpComplete}
              className={`otp-verify-btn ${!verifying && otpComplete ? "otp-verify-btn-active" : "otp-verify-btn-disabled"}`}
            >
              {verifying ? (
                <>
                  <span className="otp-spinner" />
                  Verifying...
                  <div className="btn-shimmer" />
                </>
              ) : (
                <>
                  {t("otp.verify") || "Verify"} &amp; Continue
                  <span style={{ fontWeight: 900 }}>✓</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Page footer */}
        <p className="otp-page-footer">
          🔒 &nbsp; End-to-end encrypted · Powered by Trilo
        </p>
      </div>

      {/* ── Pipeline workflow animation ── */}
      {(() => {
        const stages = [
          { icon: "📱", label: "Phone" },
          { icon: "🔐", label: "Verify" },
          { icon: "👤", label: "Details" },
          { icon: "📅", label: "Book" },
          { icon: "✅", label: "Done" },
        ];
        return (
          <div className="wf2-outer" aria-hidden="true">
            <p className="wf2-title">How it works</p>

            {/* Stage nodes + connectors */}
            <div className="wf2-stages">
              {stages.map((s, i) => (
                <React.Fragment key={i}>
                  <div className={`wf2-node${i === wfStage ? " wf2-active" : i < wfStage ? " wf2-done" : ""}`}>
                    <div className="wf2-dot">
                      {i < wfStage ? "✓" : s.icon}
                    </div>
                    <span className="wf2-node-label">{s.label}</span>
                  </div>
                  {i < 4 && (
                    <div className="wf2-connector">
                      <div className="wf2-connector-fill" style={{ width: i < wfStage ? "100%" : "0%" }} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Demo card — re-mounts per stage to restart animations */}
            <div className="wf2-demo-card">
              {wfStage === 0 && (
                <div key="s0" className="wf2-content">
                  <div className="wf2-phone-row">
                    <span className="wf2-s0-icon">📱</span>
                    <div className="wf2-phone-box">
                      <span className="wf2-phone-prefix">+91</span>
                      <span className="wf2-phone-typing">9876543210</span>
                      <span className="wf2-cursor">|</span>
                    </div>
                  </div>
                  <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, animation: "wf2FadeUp 0.4s ease 2s both" }}>
                    Enter your mobile number to begin
                  </p>
                </div>
              )}
              {wfStage === 1 && (
                <div key="s1" className="wf2-content">
                  <p className="wf2-otp-sent">OTP sent to +91 98765•••10</p>
                  <div className="wf2-otp-boxes">
                    {[3, 7, 2, 9, 1, 5].map((d, i) => (
                      <div key={i} className="wf2-otp-box">{d}</div>
                    ))}
                  </div>
                  <p className="wf2-verifying">Verifying…</p>
                </div>
              )}
              {wfStage === 2 && (
                <div key="s2" className="wf2-content">
                  <div className="wf2-field">
                    <span className="wf2-field-label">Name</span>
                    <span className="wf2-field-val">Arun Kumar</span>
                  </div>
                  <div className="wf2-field">
                    <span className="wf2-field-label">DOB</span>
                    <span className="wf2-field-val">14 Mar 1990</span>
                  </div>
                  <div className="wf2-field">
                    <span className="wf2-field-label">Gender</span>
                    <span className="wf2-field-val">Male</span>
                  </div>
                </div>
              )}
              {wfStage === 3 && (
                <div key="s3" className="wf2-content">
                  <div className="wf2-doctor-row">
                    <span className="wf2-doc-avatar">👨‍⚕️</span>
                    <div>
                      <p className="wf2-doc-name">Dr. Rajan Pillai</p>
                      <p className="wf2-doc-dept">Cardiology</p>
                    </div>
                  </div>
                  <div className="wf2-slot-chips">
                    <span className="wf2-chip wf2-chip-date">📅 12 Apr 2025</span>
                    <span className="wf2-chip wf2-chip-time">⏰ 10:30 AM</span>
                  </div>
                </div>
              )}
              {wfStage === 4 && (
                <div key="s4" className="wf2-content wf2-s4">
                  <div className="wf2-stamp">✓</div>
                  <p className="wf2-confirmed-text">Booking Confirmed!</p>
                  <p className="wf2-confirmed-hint">Token #42 · Dr. Rajan Pillai</p>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
